// CS-1758 - Facesheet Extraction Popup
// Date: 2026-03-25
// Facesheet report viewer inside the aiMedicalAudit module

import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { uploadFacesheetForExtraction } from "../../../redux/slice/AI/AiAuditSlice";
import {
    setSingleAlertObj,
    showHideLoadingDialog,
    setLoadingDialogConfig,
    resetLoadingDialogConfig,
} from "../../../redux/slice/commonSlice";
import {
    Box,
    Typography,
    useTheme,
    Grid,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import {
    Close,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import CancelIcon from "@mui/icons-material/Cancel";
import { useModalStyles } from "../../common/useModalStyles";

interface FacesheetExtractionPopupProps {
    open: boolean;
    onClose: () => void;
    // CS-1758 - Pre-loaded facesheet data (e.g. from list view)
    initialFacesheetData?: any;
    patientId?: number | string | null;
    documentId?: number | string | null;
}

interface FacesheetFormData {
    first_name: string;
    middle_name: string;
    last_name: string;
    dob: string;
    sex: string;
    street_address: string;
    unit_number: string;
    city: string;
    state: string;
    zip_code: string;
    email: string;
    primary_phone: string;
    insurance_category: string;
    insurance_name: string;
    policy_number: string;
    subscriber_name: string;
    practice_name: string;
}

const FACESHEET_FORM_DEFAULTS: FacesheetFormData = {
    first_name: "",
    middle_name: "",
    last_name: "",
    dob: "",
    sex: "",
    street_address: "",
    unit_number: "",
    city: "",
    state: "",
    zip_code: "",
    email: "",
    primary_phone: "",
    insurance_category: "",
    insurance_name: "",
    policy_number: "",
    subscriber_name: "",
    practice_name: "",
};

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const FacesheetExtractionPopup: React.FC<FacesheetExtractionPopupProps> = ({
    open,
    onClose,
    initialFacesheetData,
    documentId = null,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    const hasTriggeredLoad = useRef(false);
    const dispatch = useAppDispatch();

    // Local state
    const [facesheetData, setFacesheetData] = useState<any>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);

    const { control, reset } = useForm<FacesheetFormData>({
        defaultValues: FACESHEET_FORM_DEFAULTS,
    });

    const { cancelBtnSx } = useModalStyles();

    const populateForm = React.useCallback((data: any) => {
        // CS-1758 - Read primary insurance from insurances array instead of flat fields
        const primaryIns = Array.isArray(data.insurances) ? data.insurances.find((ins: any) => ins.type === "Primary") || data.insurances[0] : null;
        reset({
            first_name: data.first_name || "",
            middle_name: data.middle_name || "",
            last_name: data.last_name || "",
            dob: data.dob || "",
            sex: data.sex || "",
            street_address: data.street_address || "",
            unit_number: data.unit_number || "",
            city: data.city || "",
            state: data.state || "",
            zip_code: data.zip_code || "",
            email: data.email || "",
            primary_phone: data.primary_phone || "",
            // insurance_category: data.insurance_category || "",
            // insurance_name: data.insurance_name || "",
            // policy_number: data.policy_number || "",
            // subscriber_name: data.subscriber_name || "",
            insurance_category: primaryIns?.category || "",
            insurance_name: primaryIns?.name || "",
            policy_number: primaryIns?.policy_number || "",
            subscriber_name: primaryIns?.subscriber_name || "",
            practice_name: data.practice_name || "",
        });
    }, [reset]);

    // Load initial facesheet data when provided
    // CS-1758 - Load facesheet report by persisted documentId when popup is opened from saved patient data
    React.useEffect(() => {
        if (initialFacesheetData) {
            const data = initialFacesheetData.facesheet || initialFacesheetData;
            setFacesheetData(initialFacesheetData);
            populateForm(data);
        }
    }, [initialFacesheetData, populateForm]);

    React.useEffect(() => {
        const shouldTriggerWithDocumentId =
            open &&
            !!documentId &&
            !facesheetData &&
            !initialFacesheetData &&
            !uploading &&
            !hasTriggeredLoad.current;

        if (shouldTriggerWithDocumentId) {
            hasTriggeredLoad.current = true;
            setUploading(true);
            dispatch(setLoadingDialogConfig({
                variant: "aiMedicalAudit",
                title: "Processing facesheet",
                message: "We are securely processing the document to extract patient demographic and insurance information.",
            }));
            dispatch(showHideLoadingDialog(true));

            dispatch(uploadFacesheetForExtraction({
                documentId: Number(documentId),
            })).then((response: any) => {
                if (response.meta.requestStatus === "fulfilled") {
                    const result = response.payload;
                    setFacesheetData(result?.data);
                    setUploadedFilename(result?.data?.file_name || result?.data?.documentName || null);
                    const facesheet = result?.data?.facesheet || result?.data;
                    populateForm(facesheet);
                } else {
                    const message =
                        typeof response.payload === "string" && response.payload.trim() !== ""
                            ? response.payload
                            : "Unable to load the facesheet report. Please try again.";
                    dispatch(setSingleAlertObj({
                        message,
                        alertType: "error",
                    }));
                }
            }).finally(() => {
                setUploading(false);
                dispatch(showHideLoadingDialog(false));
                dispatch(resetLoadingDialogConfig());
            });
        }
    }, [
        dispatch,
        documentId,
        facesheetData,
        initialFacesheetData,
        open,
        populateForm,
        uploading,
    ]);

    // CS-1758 - Reset report viewer state when popup closes
    React.useEffect(() => {
        if (!open) {
            setFacesheetData(null);
            setUploading(false);
            setUploadedFilename(null);
            hasTriggeredLoad.current = false;
            reset(FACESHEET_FORM_DEFAULTS);
        }
    }, [open, reset]);

    // CS-1758 - Report viewer completeness summary
    const completenessPercent = facesheetData?.completeness_percent || 0;
    const missingFields = facesheetData?.missing_fields || [];

    const getCompletenessColor = (percent: number) => {
        if (percent === 100) return colors.success[400];
        if (percent >= 75) return colors.warning[400];
        return colors.error[400];
    };

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={onClose}
                aria-labelledby="facesheet-extraction-dialog"
                maxWidth="xl"
                fullWidth
                disableEnforceFocus
                sx={{
                    "& .MuiPaper-root": {
                        borderRadius: "12px",
                        p: 0,
                        border: `2px solid ${isDark ? "#464954ff" : "#e2e8f0"}`,
                        boxShadow: isDark
                            ? "0 0 20px rgba(0, 0, 0, 0.6)"
                            : "0 0 20px rgba(0, 0, 0, 0.15)",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: { xs: 1.5, sm: 2, md: 3 },
                        background: isDark
                            ? "linear-gradient(270deg, #131319ff, #1d2931)"
                            : "#ffffff",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            minHeight: { xs: "60vh", md: "70vh" },
                            maxHeight: { xs: "75vh", md: "80vh" },
                            overflow: "auto",
                            p: { xs: 1, sm: 1.5, md: 2 },
                        }}
                    >
                        {/* Header */}
                        <Box sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", md: "center" },
                            mb: 2,
                            gap: { xs: 2, md: 0 },
                        }}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    color={colors.grey[100]}
                                    sx={{ mb: 0.5, fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" } }}
                                >
                                    Facesheet Report
                                </Typography>
                                <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                                    Review extracted patient demographic and insurance information.
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: "flex",
                                gap: { xs: 1, sm: 1.5, md: 2 },
                                flexWrap: "wrap",
                                width: { xs: "100%", md: "auto" },
                            }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                                    onClick={onClose}
                                    sx={cancelBtnSx}
                                >
                                    Close
                                </Button>
                            </Box>
                        </Box>

                        {/* File Info + Completeness */}
                        {(uploadedFilename || facesheetData) && (
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    mb: 2,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Box>
                                        <Typography variant="caption" color={colors.grey[400]} sx={{ textTransform: "uppercase" }}>
                                            File Information
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600" color={colors.grey[100]}>
                                            {uploadedFilename || "Filename"}
                                        </Typography>
                                    </Box>
                                    {facesheetData && (
                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography
                                                variant="h3"
                                                fontWeight="bold"
                                                sx={{
                                                    color: getCompletenessColor(completenessPercent),
                                                    fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                                                }}
                                            >
                                                {completenessPercent}%
                                            </Typography>
                                            <Typography variant="caption" color={colors.grey[400]}>
                                                Completeness
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                    {/* Top Row: Patient Demographics + Contact Information */}
                    <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2 }}>
                        {/* Patient Demographics */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    height: "100%",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                First Name
                                            </Typography>
                                            <CustomTextField control={control} controllerName="first_name" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Middle Name
                                            </Typography>
                                            <CustomTextField control={control} controllerName="middle_name" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Last Name
                                            </Typography>
                                            <CustomTextField control={control} controllerName="last_name" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Date of Birth
                                            </Typography>
                                            <CustomTextField control={control} controllerName="dob" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Sex
                                            </Typography>
                                            <CustomTextField control={control} controllerName="sex" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Contact Information */}
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                    border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                    height: "100%",
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Street Address
                                            </Typography>
                                            <CustomTextField control={control} controllerName="street_address" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Unit/Suite
                                            </Typography>
                                            <CustomTextField control={control} controllerName="unit_number" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: "flex", gap: { xs: 1, sm: 1.5 } }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                        City
                                                    </Typography>
                                                    <CustomTextField control={control} controllerName="city" placeholder="Not specified" size="small" readOnly />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                        State
                                                    </Typography>
                                                    <CustomTextField control={control} controllerName="state" placeholder="Not specified" size="small" readOnly />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                        Zip Code
                                                    </Typography>
                                                    <CustomTextField control={control} controllerName="zip_code" placeholder="Not specified" size="small" readOnly />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Email
                                            </Typography>
                                            <CustomTextField control={control} controllerName="email" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                                Primary Phone
                                            </Typography>
                                            <CustomTextField control={control} controllerName="primary_phone" placeholder="Not specified" size="small" readOnly />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Insurance & Practice Information */}
                    <Card
                        sx={{
                            backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                            border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                            mb: 2,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                        Insurance Category
                                    </Typography>
                                    <CustomTextField control={control} controllerName="insurance_category" placeholder="Not specified" size="small" readOnly />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                        Insurance Name
                                    </Typography>
                                    <CustomTextField control={control} controllerName="insurance_name" placeholder="Not specified" size="small" readOnly />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                        Policy Number
                                    </Typography>
                                    <CustomTextField control={control} controllerName="policy_number" placeholder="Not specified" size="small" readOnly />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                        Subscriber Name
                                    </Typography>
                                    <CustomTextField control={control} controllerName="subscriber_name" placeholder="Not specified" size="small" readOnly />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color={colors.grey[400]} sx={{ mb: 0.5, display: "block", textTransform: "uppercase" }}>
                                        Practice Name
                                    </Typography>
                                    <CustomTextField control={control} controllerName="practice_name" placeholder="Not specified" size="small" readOnly />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Missing Fields */}
                    {missingFields.length > 0 && (
                        <Card
                            sx={{
                                backgroundColor: isDark ? colors.grey[900] : colors.grey[900],
                                border: `1px solid ${isDark ? colors.grey[700] : colors.grey[300]}`,
                                mb: 2,
                            }}
                        >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: colors.warning[400],
                                        textTransform: "uppercase",
                                        fontWeight: "600",
                                        display: "block",
                                        mb: 1,
                                    }}
                                >
                                    Missing Fields
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {missingFields.map((field: string, idx: number) => (
                                        <Chip
                                            key={idx}
                                            label={field}
                                            size="small"
                                            sx={{
                                                backgroundColor: "rgba(245, 158, 11, 0.15)",
                                                color: colors.warning[400],
                                                fontWeight: "500",
                                            }}
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FacesheetExtractionPopup;
